import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, CardMedia, Typography, Box, Chip, useTheme, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import { useNavigate } from 'react-router-dom';
import { formatDuration, getResumePosition, getAuthToken } from '@virtual-cinema/client-lib';

const MovieList = ({ movies, eventId, eventData }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const accessToken = getAuthToken();
    const [resumePositions, setResumePositions] = useState({});
    const [orderedMovies, setOrderedMovies] = useState([]);

    // Order movies based on contentOrder in event details
    useEffect(() => {
        // Parse event details if needed
        let eventDetails = eventData?.details;
        if (typeof eventDetails === 'string' && eventDetails) {
            try {
                eventDetails = JSON.parse(eventDetails);
            } catch (e) {
                console.error("Error parsing event details:", e);
                eventDetails = {};
            }
        }

        // If contentOrder exists in event details, use it to order the movies
        if (
            eventDetails &&
            eventDetails.contentOrder &&
            Array.isArray(eventDetails.contentOrder) &&
            movies &&
            movies.length > 0
        ) {
            // Order movies based on contentOrder
            const ordered = orderMoviesByIds(movies, eventDetails.contentOrder);
            setOrderedMovies(ordered);
        } else {
            // Otherwise just use the movies as is
            setOrderedMovies(movies || []);
        }
    }, [movies, eventData]);

    // Function to order movies based on the contentOrder array
    const orderMoviesByIds = (movieList, orderIds) => {
        // Create a map of id to movie for faster lookup
        const movieMap = movieList.reduce((map, movie) => {
            map[movie.id] = movie;
            return map;
        }, {});

        // Create ordered array based on orderIds
        const ordered = [];

        // First add movies in the specified order
        orderIds.forEach(id => {
            if (movieMap[id]) {
                ordered.push(movieMap[id]);
                delete movieMap[id]; // Remove from map to avoid duplicates
            }
        });

        // Add any remaining movies that weren't in orderIds
        Object.values(movieMap).forEach(movie => {
            ordered.push(movie);
        });

        return ordered;
    };

    // Fetch resume positions for all movies
    useEffect(() => {
        const fetchResumePositions = async () => {
            if (!accessToken || !eventId) return;

            try {
                const positions = { ...resumePositions };

                // Create an array of promises
                const promises = movies.map(async (movie) => {
                    try {
                        const { data, error } = await getResumePosition(movie.id, eventId, accessToken);
                        if (!error && data && data.has_progress) {
                            return {
                                id: movie.id,
                                position: data.position,
                                completed: data.completed || false,
                                completion_percentage: data.completion_percentage || 0
                            };
                        }
                        return null;
                    } catch (error) {
                        console.error(`Error fetching resume position for movie ${movie.id}:`, error);
                        return null;
                    }
                });

                // Wait for all promises to resolve
                const results = await Promise.all(promises);

                // Process results
                results.forEach(result => {
                    if (result) {
                        positions[result.id] = result;
                    }
                });

                setResumePositions(positions);
            } catch (error) {
                console.error('Error fetching resume positions:', error);
            }
        };

        fetchResumePositions();
    }, [movies, eventId, accessToken]);

    const getGenreArray = (genreString) => {
        if (!genreString) return [];
        return genreString.split(',').map(g => g.trim()).filter(Boolean);
    };

    // Function to determine how to display genres based on count and length
    const renderGenreChips = (genres) => {
        // If no genres, show a placeholder
        if (genres.length === 0) {
            return <Chip label="Unspecified Genre" size="small" />;
        }

        // Display approach based on number of genres
        if (genres.length <= 2) {
            // For 1-2 genres, show them all with no ellipsis
            return genres.map((genre, index) => (
                <Tooltip key={index} title={genre} arrow>
                    <Chip
                        label={genre}
                        size="small"
                        sx={{
                            maxWidth: '100%',
                            mb: 0.5,
                            mr: 0.5,
                            '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }
                        }}
                    />
                </Tooltip>
            ));
        } else {
            // For 3+ genres, show first two plus a +X more chip
            return (
                <>
                    {genres.slice(0, 2).map((genre, index) => (
                        <Tooltip key={index} title={genre} arrow>
                            <Chip
                                label={genre}
                                size="small"
                                sx={{
                                    maxWidth: '100%',
                                    mb: 0.5,
                                    mr: 0.5,
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            />
                        </Tooltip>
                    ))}
                    <Tooltip title={genres.slice(2).join(', ')} arrow>
                        <Chip
                            label={`+${genres.length - 2} more`}
                            size="small"
                            color="secondary"
                            sx={{ mb: 0.5 }}
                        />
                    </Tooltip>
                </>
            );
        }
    };

    // Function to render the watch status overlay
    const renderWatchStatusOverlay = (movie) => {
        if (!resumePositions[movie.id]) return null;

        const resumeData = resumePositions[movie.id];

        // For completed videos - use a semi-translucent overlay with "Watched" badge
        if (resumeData.completed) {
            return (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)', // semi-translucent overlay
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 5,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: '#1DB954', // Success green for "Watched"
                            color: 'white',
                            borderRadius: '4px',
                            px: 2,
                            py: 0.75,
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: '1rem' }} />
                        Watched
                    </Box>
                </Box>
            );
        }

        // For in-progress videos - show "In Progress" badge in the center
        if (resumeData.position > 0) {
            return (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)', // semi-translucent overlay
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 5,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: theme.palette.primary.main, // Use theme color for "In Progress"
                            color: 'white',
                            borderRadius: '4px',
                            px: 2,
                            py: 0.75,
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                    >
                        <PlayCircleFilledIcon sx={{ fontSize: '1rem' }} />
                        Continue Watching
                    </Box>
                </Box>
            );
        }

        return null;
    };

    // Progress bar for in-progress videos
    const renderProgressBar = (movie) => {
        if (!resumePositions[movie.id] || resumePositions[movie.id].completed) return null;

        const resumeData = resumePositions[movie.id];

        if (resumeData.position > 0) {
            const progress = resumeData.completion_percentage || 0;

            return (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        zIndex: 5,
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            width: `${progress}%`,
                            backgroundColor: theme.palette.primary.main,
                        }}
                    />
                </Box>
            );
        }

        return null;
    };

    return (
        <Grid container spacing={3}>
            {orderedMovies.map((movie) => {
                const info = movie.info || {};
                const posterUrl = movie.latest_asset?.asset_url;
                const year = info.year || (info.release_date ? new Date(info.release_date).getFullYear() : null);
                const genres = getGenreArray(info.genre);
                const hasWatchData = resumePositions[movie.id] !== undefined;
                const isCompleted = hasWatchData && resumePositions[movie.id].completed;

                return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={movie.id}>
                        <Card
                            onClick={() => navigate(`/event/${eventId}/content/${movie.id}`)}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s',
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                position: 'relative',
                                '&:hover': {
                                    transform: 'scale(1.03)',
                                    cursor: 'pointer',
                                    boxShadow: theme.shadows[4],
                                },
                                // Add a slight opacity to completed items to further differentiate them
                                ...(isCompleted && {
                                    '& .card-media': {
                                        opacity: 0.8
                                    }
                                })
                            }}
                        >
                            <Box
                                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}
                            >
                                <CardMedia
                                    component="div"
                                    className="card-media"
                                    sx={{
                                        position: 'relative',
                                        paddingTop: '66.66%',
                                        overflow: 'hidden',
                                        backgroundColor: 'black',
                                    }}
                                >
                                    {posterUrl ? (
                                        <img
                                            src={posterUrl}
                                            alt={info.title || movie.title || "Movie Poster"}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentNode.innerHTML = '<div style="position:absolute;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;"><p style="color:white;text-align:center;padding:20px;">No Poster Available</p></div>';
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <Typography variant="body2" color="white">
                                                No Poster Available
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Show watch status overlay */}
                                    {renderWatchStatusOverlay(movie)}

                                    {/* Show progress bar for in-progress videos */}
                                    {renderProgressBar(movie)}
                                </CardMedia>

                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: '1.2em',
                                                minHeight: '1.2em',
                                                flexGrow: 1,
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            {info.title || movie.title || movie.filename || "Untitled"}
                                        </Typography>
                                        {year && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    ml: 1,
                                                    color: 'text.secondary',
                                                    flexShrink: 0
                                                }}
                                            >
                                                ({year})
                                            </Typography>
                                        )}
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            mb: 2
                                        }}
                                    >
                                        {info.summary || info.description || movie.description || "No description available"}
                                    </Typography>

                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        mt: 'auto'
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            flex: 1
                                        }}>
                                            {renderGenreChips(genres)}
                                        </Box>
                                        <Typography variant="caption" sx={{
                                            flexShrink: 0,
                                            mt: 0.5
                                        }}>
                                            {formatDuration(info.duration)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Box>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default MovieList;